import { useGlobalStore } from "@/frontend/store/GlobalStore";
import { useMilestoneStore } from "@/frontend/store/MilestoneStore";
import { useRequirementsStore } from "@/frontend/store/RequirementsStore";
import { I18n } from "@/i18n/I18n";
import { useEffect, useState } from "react";
import { Badge, Button, Placeholder } from "react-bootstrap";
import { useShallow } from "zustand/react/shallow";

interface RequirementMilestoneAssignmentsProps {
  requirementId: string;
  projectId: string;
}

export function RequirementMilestoneAssignments({ requirementId, projectId }: RequirementMilestoneAssignmentsProps) {
  const milestones = useMilestoneStore(state => state.milestones);
  const fetchMilestonesByProjectId = useMilestoneStore(state => state.fetchMilestonesByProjectId);
  const assignments = useRequirementsStore(useShallow(state => state.requirmentMilestoneAssignments.filter(a => a.requirementId === requirementId)));
  const fetchRequirmentMilestoneAssignments = useRequirementsStore(state => state.fetchRequirmentMilestoneAssignments);
  const createRequirmentMilestoneAssignment = useRequirementsStore(state => state.createRequirmentMilestoneAssignment);
  const deleteRequirmentMilestoneAssignment = useRequirementsStore(state => state.deleteRequirmentMilestoneAssignment);
  const [loading, setLoading] = useState(true);
  const userClass = useGlobalStore(state => state.userClass);

  useEffect(() => {
    void fetchMilestonesByProjectId(projectId);
  }, [projectId, fetchMilestonesByProjectId]);

  useEffect(() => {
    void fetchRequirmentMilestoneAssignments(requirementId).finally(() => {
      setLoading(false);
    });
  }, [requirementId, fetchRequirmentMilestoneAssignments]);

  const assignedMilestoneIds = assignments.map(a => a.milestoneId);

  const handleToggle = async (milestoneId: string) => {
    const isSelected = assignedMilestoneIds.includes(milestoneId);

    if (isSelected) {
      const assignment = assignments.find(a => a.milestoneId === milestoneId);
      if (assignment) {
        await deleteRequirmentMilestoneAssignment(requirementId, assignment.id);
      }
    } else {
      await createRequirmentMilestoneAssignment(requirementId, milestoneId);
    }
  };

  if (loading) {
    return <RequirementMilestoneAssignmentsPlaceholder />;
  }

  const canEditMilestones = milestones.every(milestone => milestone.propelOrgId === userClass?.getOrgId());

  if ((!canEditMilestones && assignedMilestoneIds.length === 0) || milestones.length === 0) {
    return null;
  }

  return (
    <div className="mt-2">
      {!canEditMilestones && <div className="form-label-neutral">{I18n.get('milestones')}</div>}
      {canEditMilestones && <div className="form-label-neutral">{I18n.get('milestonesDescription')}</div>}
      <div className="mt-1 d-flex gap-2 flex-wrap">
        {milestones.map(milestone => {
          const isSelected = assignedMilestoneIds.includes(milestone.id);

          if(canEditMilestones) {
            return (
              <Button
                key={milestone.id}
                className={`fusa-btn-small ${isSelected ? 'btn-neutral-dark' : 'btn-neutral-middle'}`}
                onClick={() => void handleToggle(milestone.id)}
                aria-pressed={isSelected}
              >
                {milestone.name}
              </Button>
            );
          }

          if (isSelected) {
            return <Badge key={milestone.id} className="badge text-bg-dark" bg="dark">{milestone.name}</Badge>;
          }
        })}
      </div>
    </div>
  );
}

function RequirementMilestoneAssignmentsPlaceholder() {
  return (
    <div className="mt-3">
      <h5 className="text-muted fw-bold m-0">{I18n.get('milestones')}</h5>
      <small className="text-muted mb-0">{I18n.get('milestonesDescription')}</small>
      <Placeholder data-testid="loadingIndicator" className='d-flex gap-2 pt-3 observed-element' animation='glow'>
        <Placeholder.Button xs={1} size='xs' className='btn btn-secondary' />
        <Placeholder.Button xs={1} size='xs' className='btn btn-secondary' />
        <Placeholder.Button xs={1} size='xs' className='btn btn-secondary' />
      </Placeholder>
    </div>
  );
}
